import { useState } from "react";

import { sanearHorizonte } from "./horizonte";
import { HORIZONTE_MAX, HORIZONTE_MIN } from "../../lib/projecao/tipos";

/*
 * Controle de horizonte da projeção (FE-2 CP-C).
 *
 * Governa o número de dias projetados (faixa configurável, default
 * [HORIZONTE_MIN, HORIZONTE_MAX] = 1..365). O valor selecionado propaga para a
 * página, que o repassa a `useProjecaoEmpresa`/`useProjecaoGrupo`/`useDashboard`
 * — como o horizonte está na queryKey (CP-A), trocar o valor refaz a query.
 *
 * FAIXA PARAMETRIZÁVEL: `minimo`/`maximo` são OPCIONAIS, com default nas
 * constantes da projeção. A projeção (`Projecao.tsx`) não passa nada e segue em
 * 1..365; o Dashboard passa `maximo={120}` porque o backend do dashboard rejeita
 * horizonte >120 (422) — assim o FE clampa ANTES de disparar, sem 422 inútil, e
 * o `<input max>` anuncia o teto real.
 *
 * BORDA: o input é numérico, mas o usuário pode digitar fora da faixa ou um
 * valor não-numérico. NUNCA propagamos um horizonte inválido (evita disparar a
 * query com um parâmetro que o backend rejeitaria, e mantém a queryKey sã):
 *  - texto vazio/não-parseável → mantemos o último valor válido, sem emitir;
 *  - valor numérico → CLAMP em [minimo, maximo] antes de emitir.
 * O estado local do `<input>` reflete o que foi digitado (UX), mas a página só
 * recebe valores já saneados.
 */

interface ControleHorizonteProps {
  /** Horizonte atual (já saneado) governado pela página. */
  valor: number;
  /** Notifica a página de um novo horizonte VÁLIDO (dentro da faixa). */
  aoMudar: (horizonte: number) => void;
  /** Mínimo da faixa (default `HORIZONTE_MIN` = 1). */
  minimo?: number;
  /** Máximo da faixa (default `HORIZONTE_MAX` = 365). */
  maximo?: number;
}

export function ControleHorizonte({
  valor,
  aoMudar,
  minimo = HORIZONTE_MIN,
  maximo = HORIZONTE_MAX,
}: ControleHorizonteProps): JSX.Element {
  // Texto bruto do input — pode divergir temporariamente do `valor` saneado
  // enquanto o usuário digita (ex.: vazio, ou um número fora da faixa).
  const [texto, setTexto] = useState<string>(String(valor));

  function aoDigitar(evento: React.ChangeEvent<HTMLInputElement>): void {
    const bruto = evento.target.value;
    setTexto(bruto);

    const saneado = sanearHorizonte(bruto, minimo, maximo);
    if (saneado === null) {
      // Vazio ou não-parseável: não emite — mantém o último horizonte válido.
      return;
    }
    aoMudar(saneado);
  }

  return (
    <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
      <span className="font-subtitulo">Horizonte (dias)</span>
      <input
        type="number"
        inputMode="numeric"
        min={minimo}
        max={maximo}
        value={texto}
        onChange={aoDigitar}
        className="w-32 rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
      />
    </label>
  );
}
